import { motion } from "framer-motion";
import { Users, Target, Leaf, Hammer } from "lucide-react";

const sections = [
  {
    title: "Who We Are",
    icon: Users,
    content:
      "Matshinye Jr Holdings (Pty) Ltd is a proudly black-woman owned multidisciplinary consulting firm founded in 2013 and based in Mpumalanga, South Africa. We deliver innovative engineering solutions that shape sustainable infrastructure and communities.",
  },
  {
    title: "Our Strategy",
    icon: Target,
    content:
      "We focus on client-centric, sustainable engineering solutions that integrate innovation, efficiency, and operational excellence — ensuring every project drives lasting impact and growth.",
  },
  {
    title: "Sustainability & Governance",
    icon: Leaf,
    content:
      "Committed to environmental stewardship and ethical governance, we adhere to strict compliance standards while promoting social responsibility across all our projects.",
  },
  {
    title: "What We Do",
    icon: Hammer,
    content:
      "Our expertise spans infrastructure development, civil engineering, project management, and consulting services tailored to meet the unique needs of the built environment.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

const iconVariants = {
  initial: { scale: 0 },
  animate: { scale: 1, transition: { type: "spring", stiffness: 200, damping: 10 } },
};

export default function About() {
  return (
    <section id="about" className="relative mx-auto max-w-7xl px-6 py-32 bg-gradient-to-br from-primary to-primary-dark text-text-light overflow-hidden">
      {/* Decorative background glow */}
      <div className="absolute -top-10 -left-20 h-96 w-96 bg-accent blur-3xl opacity-30 rounded-full z-0" />
      <div className="absolute -bottom-10 -right-20 h-96 w-96 bg-accent blur-3xl opacity-30 rounded-full z-0" />

      {/* Animated Heading */}
      <motion.h2
        initial={{ opacity: 0, y: -40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="relative z-10 mb-20 text-center font-heading text-5xl md:text-6xl font-extrabold text-accent leading-tight max-w-4xl mx-auto drop-shadow-xl"
      >
        Building Tomorrow, Today
      </motion.h2>

      {/* Grid Cards */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.25,
            },
          },
        }}
        className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16"
      >
        {sections.map(({ title, icon: Icon, content }, i) => (
          <motion.div
            key={title}
            variants={cardVariants}
            className="relative bg-white/5 backdrop-blur-md rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/10"
          >
            {/* Icon */}
            <motion.div
              variants={iconVariants}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary border-4 border-accent text-accent shadow-md"
            >
              <Icon className="h-8 w-8" />
            </motion.div>

            {/* Content */}
            <h3 className="text-2xl md:text-3xl font-semibold text-accent mb-4">
              {title}
            </h3>
            <p className="text-base md:text-lg text-text-light leading-relaxed">
              {content}
            </p>

            {/* Animated Divider */}
            <motion.div
              className="mt-6 h-1 w-1/3 bg-accent rounded-full"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              style={{ transformOrigin: i % 2 === 0 ? "left" : "right" }}
            />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
